import React, { useId, useRef, useState } from 'react';
import { Select, Typography } from 'antd';
import { ChevronDown, Mic } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer, Cell } from 'recharts';
import {
  COMPETENCY_BY_ID,
  FRAMEWORKS,
  MAX_DIMENSION_SCORE,
  MAX_STRUCTURE_SCORE,
  sectionTiming,
} from '@evaltick/shared';
import type { CoachDimensionScore, ItemScoreTrend, QuestionType, StructureComponentScore } from '@evaltick/shared';
import { TrendChart } from '@/components/Interview/TrendChart';
import type { ChartDatum } from '@/components/Interview/TrendChart';
import { formatDurationClock, scoreToLabel, toneClass } from '@/utils';
import { useCountUp } from '@/hooks';
import styles from './AnswerBreakdown.module.scss';

const { Text } = Typography;

interface AnswerBreakdownProps {
  itemTrend: ItemScoreTrend | null | undefined;
  dimensionScores: CoachDimensionScore[] | null | undefined;
  competencyId: string;
  questionType: QuestionType;
  structureScore: number | null | undefined;
  /** Aggregate structure feedback. Only surfaced for responses scored before
   * per-component scoring existed — those carry their own rationale, which
   * StructureRadial already shows section by section. */
  structureRationale: string | null | undefined;
  structureComponentScores: StructureComponentScore[] | null | undefined;
}

const NOT_ASSESSED = 'Not assessed';
const REVEAL_STEP_MS = 45;
const MIN_INSIDE_SCORE = 16;
const FEEDBACK_LABEL = 'Feedback:';
const RADIAL_HEIGHT = 200;
const RADIAL_INNER_RADIUS = '32%';
const RADIAL_OUTER_RADIUS = '100%';
const RADIAL_START_ANGLE = 90;
const RADIAL_END_ANGLE = -270;
const RADIAL_CORNER_RADIUS = 4;
const RADIAL_ANIMATION_MS = 900;
const TREND_CHART_HEIGHT = 190;
const MIN_TREND_ATTEMPTS = 2;
const DIMENSION_SUBTITLE =
  'How this answer scored on each part of the competency, weakest first — the one to work on is highlighted.';
const TREND_SUBTITLE = 'How this question has scored across your attempts. Switch metric to follow a single dimension.';
const structureSubtitle = (frameworkName: string) =>
  `How closely the answer followed the ${frameworkName} structure, section by section, and how long each one ran.`;
const GRADIENT_START_OPACITY = 0.5;
const UNFOCUSED_OPACITY = 0.9;

const RING_COLORS = [
  'var(--color-purple-light-1)',
  'var(--color-purple-light-2)',
  'var(--color-purple-light)',
  'var(--color-periwinkle-2)',
  'var(--color-periwinkle)',
];

const ringColor = (index: number) => RING_COLORS[index % RING_COLORS.length];

const TREND_COLOR = RING_COLORS[2];

/** Sentinel for the question's own score series in QuestionTrend's selector —
 * prefixed so it can never be mistaken for a competency dimension key. */
const OVERALL_TREND_KEY = '__overall__';


const byScoreAscending = (a: CoachDimensionScore, b: CoachDimensionScore) =>
  (a.score ?? Number.POSITIVE_INFINITY) - (b.score ?? Number.POSITIVE_INFINITY);

const withoutLabelPrefix = (rationale: string, label: string) =>
  rationale.startsWith(`${label}:`) ? rationale.slice(label.length + 1).trim() : rationale;

const swatchColor = (color: string) => ({ '--swatch': color }) as React.CSSProperties;

const segmentStyle = (color: string, grow: number) =>
  ({ '--segment-color': color, flexGrow: grow }) as React.CSSProperties;

const barWidth = (score: number) =>
  ({ '--bar-width': `${(score / MAX_DIMENSION_SCORE) * 100}%` }) as React.CSSProperties;

interface DimensionRowProps {
  label: string;
  dimension: CoachDimensionScore;
  isWeakest: boolean;
  index: number;
}

const DimensionRow: React.FC<DimensionRowProps> = ({ label, dimension, isWeakest, index }) => {
  const [expanded, setExpanded] = useState(isWeakest);
  const revealedScore = useCountUp(dimension.score ?? 0, { delayMs: index * REVEAL_STEP_MS });

  if (dimension.score == null) {
    return (
      <div className={`${styles.plotRow} ${styles.plotRowUnscored}`}>
        <span className={styles.plotName}>{label}</span>
        <Text type="secondary" className={styles.plotValue}>
          {NOT_ASSESSED}
        </Text>
      </div>
    );
  }

  const score = dimension.score;

  return (
    <div className={`${styles.plotGroup} ${toneClass(styles, score)}`}>
      <button
        type="button"
        className={`${styles.plotRow} ${styles.plotRowButton}`}
        aria-expanded={expanded}
        onClick={() => setExpanded((open) => !open)}
      >
        <span className={styles.plotName}>{label}</span>
        <ChevronDown className={styles.plotChevron} aria-hidden="true" />
        <span className={styles.plotTrack}>
          <span
            className={`${styles.plotFill} ${isWeakest ? styles.plotFillWeakest : ''}`}
            style={barWidth(revealedScore)}
          >
            <span
              className={`${styles.plotFigure} ${score < MIN_INSIDE_SCORE ? styles.plotFigureOutside : ''}`}
            >
              {Math.round(revealedScore)}
              <small>%</small>
            </span>
          </span>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="rationale"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className={styles.plotReveal}
          >
            <p className={styles.plotRationale}>
              <span className={styles.plotBand}>{scoreToLabel(score)}</span>
              {dimension.rationale}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

interface StructureComponent {
  key: string;
  label: string;
  score: number;
  rationale: string;
  ms: number | null;
  estimated: boolean;
  color: string;
}

const SectionHeader: React.FC<{
  title: string;
  subtitle: string;
  figure?: React.ReactNode;
  control?: React.ReactNode;
}> = ({ title, subtitle, figure, control }) => (
  <header className={styles.sectionHead}>
    <div className={styles.sectionHeadMain}>
      <h4 className={styles.sectionTitle}>{title}</h4>
      {figure}
      {control}
    </div>
    <p className={styles.sectionSubtitle}>{subtitle}</p>
  </header>
);

const ComponentTiming: React.FC<{ component: StructureComponent }> = ({ component }) =>
  component.ms == null ? null : (
    <span className={`${styles.timing} ${component.estimated ? '' : styles.timingMeasured}`}>
      {!component.estimated && <Mic aria-hidden="true" />}~{formatDurationClock(component.ms)}
      {component.estimated && <em>(estimated)</em>}
    </span>
  );

const StructureRadial: React.FC<{ components: StructureComponent[] }> = ({ components }) => {
  const weakest = components.reduce(
    (lowest, current) => (current.score < lowest.score ? current : lowest),
    components[0]
  );
  const [focusedKey, setFocusedKey] = useState(weakest.key);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const reduceMotion = useReducedMotion();
  const gradientPrefix = useId();
  const gradientId = (key: string) => `${gradientPrefix}-${key}`.replace(/[^\w-]/g, '');
  const chartRef = useRef<HTMLDivElement>(null);

  const sectorIndexFrom = (event: React.MouseEvent) => {
    const sector = (event.target as Element).closest('.recharts-radial-bar-sector');
    const sectors = [...(chartRef.current?.querySelectorAll('.recharts-radial-bar-sector') ?? [])];
    return sector ? sectors.indexOf(sector) : -1;
  };

  const trackHover = (event: React.MouseEvent) => {
    const index = sectorIndexFrom(event);
    setHoveredKey(index >= 0 ? components[index].key : null);
  };

  const focusFromChart = (event: React.MouseEvent) => {
    const index = sectorIndexFrom(event);
    if (index >= 0) setFocusedKey(components[index].key);
  };

  const data = components.map((component) => ({
    key: component.key,
    label: component.label,
    score: component.score,
    fill: component.color,
  }));

  return (
    <div className={styles.radial}>
      <div
        ref={chartRef}
        className={styles.chartHitArea}
        onMouseOver={trackHover}
        onMouseLeave={() => setHoveredKey(null)}
        onClick={focusFromChart}
      >
        <ResponsiveContainer width="100%" height={RADIAL_HEIGHT}>
        <RadialBarChart
          data={data}
          innerRadius={RADIAL_INNER_RADIUS}
          outerRadius={RADIAL_OUTER_RADIUS}
          startAngle={RADIAL_START_ANGLE}
          endAngle={RADIAL_END_ANGLE}
        >
          <defs>
            {data.map((datum) => (
              <linearGradient key={datum.key} id={gradientId(datum.key)} x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={datum.fill} stopOpacity={GRADIENT_START_OPACITY} />
                <stop offset="100%" stopColor={datum.fill} stopOpacity={1} />
              </linearGradient>
            ))}
          </defs>
          <PolarAngleAxis type="number" domain={[0, MAX_STRUCTURE_SCORE]} tick={false} />
          <RadialBar
            dataKey="score"
            background={{ fill: 'var(--color-bg-muted)' }}
            cornerRadius={RADIAL_CORNER_RADIUS}
            isAnimationActive={!reduceMotion}
            animationDuration={RADIAL_ANIMATION_MS}
            animationEasing="ease-out"
          >
            {data.map((datum) => (
              <Cell
                key={datum.key}
                fill={`url(#${gradientId(datum.key)})`}
                fillOpacity={datum.key === focusedKey || datum.key === hoveredKey ? 1 : UNFOCUSED_OPACITY}
              />
            ))}
          </RadialBar>
          </RadialBarChart>
        </ResponsiveContainer>
      </div>

      <ul className={styles.legend}>
        {components.map((component) => {
          const focused = component.key === focusedKey;
          return (
            <li key={component.key}>
              <button
                type="button"
                className={`${styles.legendItem} ${focused ? styles.legendItemFocused : ''} ${
                  component.key === hoveredKey ? styles.legendItemHovered : ''
                }`}
                aria-pressed={focused}
                onClick={() => setFocusedKey(component.key)}
                onMouseEnter={() => setHoveredKey(component.key)}
                onMouseLeave={() => setHoveredKey(null)}
                onFocus={() => setHoveredKey(component.key)}
                onBlur={() => setHoveredKey(null)}
              >
                <span className={styles.legendSwatch} style={swatchColor(component.color)} />
                <span className={styles.legendLabel}>{component.label}</span>
                <ComponentTiming component={component} />
                <span className={styles.figure}>
                  {component.score}
                  <small> / {MAX_STRUCTURE_SCORE}</small>
                </span>
              </button>
              <AnimatePresence initial={false}>
                {focused && (
                  <motion.div
                    key="detail"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className={styles.plotReveal}
                  >
                    <p className={styles.legendRationale}>
                      <span className={styles.legendRationaleLabel}>{FEEDBACK_LABEL}</span>{' '}
                      {withoutLabelPrefix(component.rationale, component.label)}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const TimeRibbon: React.FC<{ sections: (StructureComponent & { ms: number })[] }> = ({ sections }) => (
  <>
    <div className={styles.ribbon}>
      {sections.map((section) => (
        <span
          key={section.key}
          className={`${styles.segment} ${section.estimated ? styles.segmentEstimated : ''}`}
          style={segmentStyle(section.color, section.ms)}
        >
          <abbr title={section.label}>{section.label.charAt(0)}</abbr>
        </span>
      ))}
    </div>
    <div className={styles.ribbonKey}>
      <span>
        <i className={styles.keySwatch} aria-hidden="true" />
        measured
      </span>
      <span>
        <i className={`${styles.keySwatch} ${styles.keyEstimated}`} aria-hidden="true" />
        (estimated)
      </span>
    </div>
  </>
);

const QuestionTrend: React.FC<{
  itemTrend: ItemScoreTrend;
  dimensionLabels: Record<string, string>;
}> = ({ itemTrend, dimensionLabels }) => {
  const dimensionKeys = [
    ...new Set(itemTrend.attempts.flatMap((a) => a.dimensionScores?.map((d) => d.key) ?? [])),
  ];
  // The question's own score history is what this chart is for — a dimension is
  // the thing you switch *to*. Sentinel-prefixed so it can't collide with a
  // real dimension key.
  const seriesKeys = [OVERALL_TREND_KEY, ...dimensionKeys];
  const [seriesKey, setSeriesKey] = useState(OVERALL_TREND_KEY);
  const label = (key: string) =>
    key === OVERALL_TREND_KEY ? 'Overall' : (dimensionLabels[key] ?? key.replace(/-/g, ' '));

  const data: ChartDatum[] = itemTrend.attempts.map((a) => ({
    attempt: a.attemptNumber,
    score:
      seriesKey === OVERALL_TREND_KEY
        ? a.overallScore
        : (a.dimensionScores?.find((d) => d.key === seriesKey)?.score ?? null),
    carried: a.isCarriedOver,
  }));

  return (
    <section className={`${styles.section} ${styles.sectionTrend}`}>
      <SectionHeader title="Score Trend" subtitle={TREND_SUBTITLE} />
      {seriesKeys.length > 1 && (
        <div className={styles.trendControl}>
          <Select
            size="small"
            className={styles.trendSelect}
            popupClassName={styles.trendPopup}
            popupMatchSelectWidth={false}
            value={seriesKey}
            onChange={setSeriesKey}
            aria-label="Metric shown in the score trend"
            options={seriesKeys.map((key) => ({ value: key, label: label(key) }))}
          />
        </div>
      )}
      <TrendChart
        data={data}
        series={[{ dataKey: 'score', label: label(seriesKey), color: TREND_COLOR }]}
        height={TREND_CHART_HEIGHT}
      />
    </section>
  );
};

export const AnswerBreakdown: React.FC<AnswerBreakdownProps> = ({
  itemTrend,
  dimensionScores,
  competencyId,
  questionType,
  structureScore,
  structureRationale,
  structureComponentScores,
}) => {
  if (!dimensionScores || dimensionScores.length === 0) return null;

  const dimensionDefs = COMPETENCY_BY_ID[competencyId]?.questionTypes[questionType]?.dimensions ?? [];
  const dimensionLabels = Object.fromEntries(dimensionDefs.map((d) => [d.key, d.label]));

  const framework = FRAMEWORKS[questionType];
  const componentLabels = Object.fromEntries(framework.components.map((c) => [c.key, c.label]));

  const ranked = [...dimensionScores].sort(byScoreAscending);
  const weakest = ranked.find((d) => d.score != null);

  const components: StructureComponent[] = (structureComponentScores ?? []).map((component, index) => {
    const timing = sectionTiming(component);
    return {
      key: component.key,
      label: componentLabels[component.key] ?? component.key.replace(/-/g, ' '),
      score: component.score,
      rationale: component.rationale,
      ms: timing?.ms ?? null,
      estimated: timing?.estimated ?? false,
      color: ringColor(index),
    };
  });
  const timedSections = components.filter(
    (component): component is typeof component & { ms: number } => component.ms != null
  );
  const hasStructure = structureScore != null || components.length > 0;

  return (
    <div className={`${styles.breakdown} ${hasStructure ? styles.breakdownSplit : ""}`}>
      <section className={styles.section}>
        <SectionHeader title="Dimension Breakdown" subtitle={DIMENSION_SUBTITLE} />
        <div className={styles.plot}>
          {ranked.map((dimension, index) => (
            <DimensionRow
              key={dimension.key}
              index={index}
              label={dimensionLabels[dimension.key] ?? dimension.key.replace(/-/g, ' ')}
              dimension={dimension}
              isWeakest={dimension === weakest}
            />
          ))}
        </div>
      </section>

      {hasStructure && (
        <section className={styles.section}>
          <SectionHeader
            title={`${framework.name} Structure Breakdown`}
            subtitle={structureSubtitle(framework.name)}
            figure={
              structureScore != null ? (
                <span className={styles.figure}>
                  {structureScore}
                  <small> / {MAX_STRUCTURE_SCORE}</small>
                </span>
              ) : undefined
            }
          />

          {components.length > 0 && <StructureRadial components={components} />}

          {components.length === 0 && structureRationale && (
            <p className={styles.rationale}>{structureRationale}</p>
          )}

          {timedSections.length > 0 && <TimeRibbon sections={timedSections} />}
        </section>
      )}

      {itemTrend && itemTrend.attempts.length >= MIN_TREND_ATTEMPTS && (
        <QuestionTrend itemTrend={itemTrend} dimensionLabels={dimensionLabels} />
      )}
    </div>
  );
};
